import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import dayjs, {Dayjs} from 'dayjs';
import {type FC, useState} from 'react';
import {useTranslation} from 'react-i18next';
import DialogPanel from '@/components/common/DialogPanel';
import CalendarPicker from '@/components/common/forms/CalendarPicker';
import InputNumber from '@/components/common/forms/InputNumber';
import InputText from '@/components/common/forms/InputText';
import Select from '@/components/common/forms/Select';
import Label from '@/components/common/Label';
import type {LidarTechnology} from '@/domain/entities/LidarTechnology';
import type {UavPointCloud, UavPointCloudCreate} from '@/domain/entities/UavPointCloud';

//TYPES
type Props = {
  lidarTechnologies?: LidarTechnology[];
  onUploadPointCloud?: (pointCloud: Partial<UavPointCloudCreate>, file: File) => void;
  onClose: () => void;
};

const UavPointCloudUploadingDialog: FC<Props> = ({lidarTechnologies, onUploadPointCloud, onClose}) => {
  const {t} = useTranslation();

  const initialPointCloud: Partial<UavPointCloud> = {flightDate: dayjs()};
  const [partialEntity, setPartialEntity] = useState<Partial<UavPointCloud>>(initialPointCloud);
  const [file, setFile] = useState<File>();

  const handleDate = (flightDate?: Dayjs) => flightDate && setPartialEntity({...partialEntity, flightDate});
  const handleLidarTechnology = (optionUuid?: string) => {
    const technology = lidarTechnologies?.find((t) => t.uuid === optionUuid);
    setPartialEntity({
      ...partialEntity,
      lidarTechnologyUuid: optionUuid,
      height: technology?.height,
      speed: technology?.speed,
      overlapPercentage: technology?.overlapPercentage,
    });
  };
  const handleHeight = (height: number) => setPartialEntity({...partialEntity, height});
  const handleSpeed = (speed: number) => setPartialEntity({...partialEntity, speed});
  const handleOverlapPercentage = (overlapPercentage: number) =>
    setPartialEntity({...partialEntity, overlapPercentage});
  const handleComments = (comments: string) => setPartialEntity({...partialEntity, comments});

  const handleUpload = () => {
    if (file && partialEntity && onUploadPointCloud) {
      onUploadPointCloud(partialEntity as UavPointCloudCreate, file);
    }
  };

  const hasAllPointCloudsProperties =
    partialEntity.flightDate &&
    partialEntity.lidarTechnologyUuid &&
    partialEntity.height &&
    partialEntity.speed &&
    partialEntity.overlapPercentage;

  const isUploadEnabled = hasAllPointCloudsProperties && file;

  return (
    <DialogPanel isOpen={true} label={t('pointClouds.title')} onClose={onClose}>
      <Stack direction="column" spacing={2} sx={{maxWidth: '350px', p: 1}}>
        <Typography>{t('pointClouds.helperText')}</Typography>
        <Label value={t('pointClouds.flightDate')} required={true}>
          <CalendarPicker value={partialEntity ? partialEntity.flightDate : new Dayjs()} onChange={handleDate} />
        </Label>
        <Label value={t('pointClouds.lidarTechnology')} required={true}>
          <Select
            options={lidarTechnologies ? lidarTechnologies : []}
            selectedOptionId={partialEntity?.lidarTechnologyUuid}
            onChange={handleLidarTechnology}
          />
        </Label>
        <Stack direction="row" spacing={2}>
          <Label value={t('pointClouds.height')} required={true}>
            <InputNumber value={partialEntity?.height} required={true} onChange={handleHeight} />
          </Label>
          <Label value={t('pointClouds.speed')} required={true}>
            <InputNumber value={partialEntity?.speed} required={true} onChange={handleSpeed} />
          </Label>
        </Stack>
        <Label value={t('pointClouds.overlapPercentage')} required={true}>
          <InputNumber
            value={partialEntity?.overlapPercentage}
            required={true}
            onChange={handleOverlapPercentage}
            min={0}
            max={100}
          />
        </Label>
        <Label value={t('pointClouds.comments')}>
          <InputText value={partialEntity?.comments} required={true} onChange={handleComments} multiline={true} />
        </Label>
        <Button component="label" variant="outlined" onClick={() => {}}>
          {file?.name || t('pointClouds.file')}
          <input type="file" accept=".laz,.las" onChange={(event) => setFile(event.target.files?.[0])} hidden />
        </Button>
        <Button variant="outlined" disabled={!isUploadEnabled} onClick={handleUpload}>
          {t('pointClouds.upload')}
        </Button>
      </Stack>
    </DialogPanel>
  );
};
export default UavPointCloudUploadingDialog;
